/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: recording-level analysis quality gate (core-viewer.js 2605-2894).
//
// Deviations from the prototype (see MODULE-MAP.md):
// - CANONICAL exam-viewer mode is hard-enabled: the 4x-duplicated APP_VERSION
//   allow-list (`canonicalEnergyV071`) is deleted; the canonical branches
//   (no 3.0s minimum on energy runs, classifyPhaseEnergyDisplay path) always run.
//   The legacy branches are removed, not preserved behind a flag.
// - The `D._analysisQuality` memo (which the prototype forgot to invalidate when
//   phase review resolved — a known latent bug) is replaced by a cache on the
//   immutable analysis context: `ctx` carries a fixed `phaseReviewResolution`, so
//   a context is created per resolution state and the memo can never go stale.
//
// Provisional Energy (prototype v0.8.3.18 at pcg-viewer-mvp git 6b385e4): a
// recording with a usable Timing run of at least MIN_USABLE_CYCLES but fewer
// than MIN_ENERGY_RELIABLE_CYCLES cycles is flagged `visualEnergyProvisional`
// (phase status "limited_provisional_energy") and offers that run as its
// display Energy cycles, for a provisional estimate that needs manual review.
// Before, such a recording was "limited_visible_energy", and only when the
// murmur band carried substantial energy. The strict evidence path
// (`energyAllowed` / `energyCycles`, which the report export reads) is
// unchanged: it still needs MIN_ENERGY_RELIABLE_CYCLES — and so does scoring
// itself (acousticActivityForCycles), so the provisional run yields no
// activity numbers yet.

import { median } from "./stats"
import { rejectGate, isGateHardRejected } from "./gate"
import {
  timingCycles,
  visualTimingCycles,
  localNoiseReason,
  lowConfidenceMarker,
  longestUsableRun,
  longestReliableRun,
  mergeWindows,
  windowCoveragePct,
  cycleRunDuration,
  cycleTotalDuration,
  regularPctForCycles,
  markerConfidenceForCycles,
  segmentRangeForCycles,
  cycleAuditIds,
} from "./cycles"
import { murmurBandEnergyEvidence } from "./energy"
import { MIN_USABLE_CYCLES, MIN_ENERGY_RELIABLE_CYCLES, ENERGY_COVERAGE_DELTA } from "./constants"

export function classifyPhaseEnergyDisplay(
  selectedTimingCycleCount,
  substantialEnergy,
  phaseScoringEligible,
  phaseAssignmentLimited = false
) {
  const cycleCount = Math.max(0, Number(selectedTimingCycleCount) || 0)
  if (cycleCount >= MIN_ENERGY_RELIABLE_CYCLES && phaseScoringEligible) {
    return {
      status: "scored",
      showPhaseValues: true,
      limitedVisibleEnergy: false,
      phaseAssignmentLimited: !!phaseAssignmentLimited,
    }
  }
  if (cycleCount >= MIN_USABLE_CYCLES && cycleCount < MIN_ENERGY_RELIABLE_CYCLES) {
    return {
      status: "limited_provisional_energy",
      showPhaseValues: true,
      limitedVisibleEnergy: true,
      phaseAssignmentLimited: true,
    }
  }
  return {
    status: "unavailable",
    showPhaseValues: false,
    limitedVisibleEnergy: false,
    phaseAssignmentLimited: false,
  }
}

// ctx: analysis context from createPcgCore — { payload, murmurTrace, phaseReviewResolution, cache }.
export function analysisQuality(ctx) {
  if (ctx.cache.analysisQuality) return ctx.cache.analysisQuality
  const D = ctx.payload
  const gate = rejectGate(D)
  // TODO(display-contract): this substring test on the gate's FREE-TEXT
  // `reason` is the sole trigger that withholds phase-linked Energy on 13 of
  // the 43 fixture-corpus recordings (the whole CirCor benchmark set among
  // them). The phrasing varies across payloads and a wording change in the
  // Python gate would silently re-enable Energy for a third of the corpus.
  // Behavior is ported verbatim on purpose; it should key off a structured
  // gate field once the pipeline exposes one.
  const limitedByAtypicalBurst =
    gate &&
    gate.status === "limited" &&
    String(gate.reason || "")
      .toLowerCase()
      .includes("atypical")
  if (isGateHardRejected(gate)) {
    const noiseWindows = [{ start: 0, end: D.duration, reasons: [gate.reason || "Reject Gate failed"] }]
    ctx.cache.analysisQuality = {
      status: "unreliable",
      label: "Unreliable",
      signalLabel: "Unreliable",
      cycles: [],
      visualCycles: [],
      usableCycles: [],
      visualUsableCycles: [],
      reliableCycles: [],
      summaryCycles: [],
      energyCycles: [],
      visualEnergyCycles: [],
      longestRun: 0,
      longestReliableRun: 0,
      timingSeconds: 0,
      energySeconds: 0,
      visualEnergySeconds: 0,
      reliableSeconds: 0,
      markerConfidence: 0,
      signalReliable: false,
      timingAllowed: false,
      energyAllowed: false,
      visualEnergyAllowed: false,
      usablePct: 0,
      noisyPct: 100,
      noiseWindows,
      guideQualityWindows: noiseWindows,
      summaryAllowed: false,
      rawSummaryAllowed: false,
      reason: gate.reason || "Recording rejected by input-validity gate",
      energyReason: "Recording rejected by input-validity gate",
    }
    return ctx.cache.analysisQuality
  }
  const cycles = timingCycles(D)
  const visualCycles = visualTimingCycles(D, ctx.phaseReviewResolution)
  if (!cycles.length) {
    const noiseWindows = [{ start: 0, end: D.duration, reasons: ["no usable cycle run"] }]
    ctx.cache.analysisQuality = {
      status: "unreliable",
      label: "Unreliable",
      signalLabel: "Unreliable",
      usableCycles: [],
      summaryCycles: [],
      energyCycles: [],
      longestRun: 0,
      timingSeconds: 0,
      energySeconds: 0,
      reliableSeconds: 0,
      markerConfidence: 0,
      signalReliable: false,
      timingAllowed: false,
      energyAllowed: false,
      usablePct: 0,
      noisyPct: windowCoveragePct(noiseWindows, D.duration),
      noiseWindows,
      guideQualityWindows: noiseWindows,
      summaryAllowed: false,
      reason: "No usable complete S1-S2-S1 cycle run",
    }
    return ctx.cache.analysisQuality
  }
  const cycleMed = median(cycles.map((c) => c.cycle))
  const guideQualityWindows = []
  const noiseWindows = []
  function annotateCycleQuality(cycleList, guideWindows) {
    cycleList.forEach((c) => {
      const reasons = localNoiseReason(c, cycleMed)
      c.usable = reasons.length === 0
      c.noiseReasons = reasons
      c.lowConfidenceCount = [c.s1, c.s2, c.nextS1].filter(lowConfidenceMarker).length
      c.reliable = c.usable && c.lowConfidenceCount === 0
      if (!c.usable) guideWindows.push({ start: c.s1.t, end: c.nextS1.t, reasons })
    })
  }
  annotateCycleQuality(cycles, guideQualityWindows)
  visualCycles.forEach((c) => {
    const reasons = localNoiseReason(c, cycleMed)
    c.usable = reasons.length === 0
    c.noiseReasons = reasons
    c.lowConfidenceCount = [c.s1, c.s2, c.nextS1].filter(lowConfidenceMarker).length
    c.reliable = c.usable && c.lowConfidenceCount === 0
  })
  for (let i = 0; i < D.peaks.length - 1; i++) {
    const a = D.peaks[i],
      b = D.peaks[i + 1]
    const gap = b.t - a.t
    if (cycleMed && gap > 1.55 * cycleMed) {
      guideQualityWindows.push({ start: a.t, end: b.t, reasons: ["timing gap"] })
    }
  }
  const usableCycles = cycles.filter((c) => c.usable)
  const visualUsableCycles = visualCycles.filter((c) => c.usable)
  const bestRun = longestUsableRun(cycles)
  const bestReliableRun = longestReliableRun(cycles)
  const reliableCycles = bestReliableRun
  const highConfidenceTimingCycles = cycles.filter((c) => c.usable && c.lowConfidenceCount === 0)
  const timingSummaryCandidate =
    highConfidenceTimingCycles.length >= MIN_USABLE_CYCLES ? highConfidenceTimingCycles : usableCycles
  const timingSeconds = cycleTotalDuration(
    timingSummaryCandidate.length >= MIN_USABLE_CYCLES ? timingSummaryCandidate : []
  )
  const timingAllowed = timingSummaryCandidate.length >= MIN_USABLE_CYCLES && timingSeconds >= 3.0
  const timingSummarySeconds = cycleTotalDuration(
    timingSummaryCandidate.length >= MIN_USABLE_CYCLES ? timingSummaryCandidate : []
  )
  const timingSummaryAllowed = timingAllowed && highConfidenceTimingCycles.length >= MIN_USABLE_CYCLES
  const timingSummaryCycles = timingAllowed ? timingSummaryCandidate : []
  const coverage = D.metrics && typeof D.metrics.coverage === "number" ? D.metrics.coverage : null
  const coverageOk =
    coverage === null || (coverage >= 1.0 - ENERGY_COVERAGE_DELTA && coverage <= 1.0 + ENERGY_COVERAGE_DELTA)
  const selectedTimingCycleIds = cycleAuditIds(timingSummaryCycles)
  const reliableRegularPct = regularPctForCycles(timingSummaryCycles)
  const energyInterpretableRun =
    !limitedByAtypicalBurst &&
    timingAllowed &&
    timingSummaryCycles.length >= MIN_ENERGY_RELIABLE_CYCLES &&
    coverageOk &&
    (reliableRegularPct === null || reliableRegularPct >= 65.0)
      ? timingSummaryCycles
      : []
  const visualEnergyPhaseLimited =
    limitedByAtypicalBurst && timingAllowed && timingSummaryCycles.length >= MIN_ENERGY_RELIABLE_CYCLES
  const visualEnergyRun = timingAllowed && timingSummaryCycles.length >= MIN_USABLE_CYCLES ? timingSummaryCycles : []
  const murmurEnergyEvidence = murmurBandEnergyEvidence(ctx.murmurTrace, D.duration)
  const selectedTimingCycleCount = selectedTimingCycleIds.length
  const energySeconds = cycleRunDuration(
    energyInterpretableRun.length >= MIN_ENERGY_RELIABLE_CYCLES ? energyInterpretableRun : []
  )
  const visualEnergySeconds = cycleRunDuration(
    visualEnergyRun.length >= MIN_ENERGY_RELIABLE_CYCLES ? visualEnergyRun : []
  )
  // Canonical exam-viewer mode (hard-enabled): no legacy 3.0s minimum on energy runs.
  const energyAllowed = energyInterpretableRun.length >= MIN_ENERGY_RELIABLE_CYCLES
  const visualEnergyAllowed = visualEnergyRun.length >= MIN_ENERGY_RELIABLE_CYCLES
  const visualEnergyProvisional =
    timingAllowed && visualEnergyRun.length >= MIN_USABLE_CYCLES && visualEnergyRun.length < MIN_ENERGY_RELIABLE_CYCLES
  const phaseEnergyDisplay = classifyPhaseEnergyDisplay(
    selectedTimingCycleCount,
    !!murmurEnergyEvidence.substantial,
    visualEnergyAllowed,
    visualEnergyPhaseLimited
  )
  const visualEnergyLimited = phaseEnergyDisplay.limitedVisibleEnergy
  const reliableSeconds = energyAllowed ? energySeconds : timingSeconds
  const rawMarkerConfidence = timingSummaryCycles.length
    ? markerConfidenceForCycles(timingSummaryCycles)
    : D.metrics && typeof D.metrics.pct_high_conf === "number"
    ? D.metrics.pct_high_conf
    : 0
  const timingCycleConfidence =
    timingSummaryCycles.length >= MIN_USABLE_CYCLES
      ? Math.max(0.7, Math.min(1, 0.7 + 0.3 * ((timingSummaryCycles.length - MIN_USABLE_CYCLES) / 5)))
      : 0
  const markerConfidence = Math.max(0, Math.min(1, rawMarkerConfidence * timingCycleConfidence))
  const energyMarkerConfidence = energyInterpretableRun.length ? markerConfidenceForCycles(energyInterpretableRun) : 0
  const visualEnergyMarkerConfidence = visualEnergyRun.length ? markerConfidenceForCycles(visualEnergyRun) : 0
  const timingSegment = segmentRangeForCycles(timingSummaryCycles)
  const energyCycleIds = cycleAuditIds(energyInterpretableRun)
  const visualEnergyCycleIds = cycleAuditIds(visualEnergyRun)
  const energySegment = segmentRangeForCycles(energyInterpretableRun)
  const visualEnergySegment = segmentRangeForCycles(visualEnergyRun)
  const signalReliable = timingAllowed && energyAllowed
  const usablePct = (100 * usableCycles.length) / cycles.length
  const mergedGuideQualityWindows = mergeWindows(guideQualityWindows)
  const mergedNoiseWindows = mergeWindows(noiseWindows)
  const noisyCoveragePct = windowCoveragePct(mergedNoiseWindows, D.duration)
  let label = "Fair"
  let signalLabel = "Fair"
  let status = "fair"
  if (signalReliable) {
    label = "Reliable"
    signalLabel = "Reliable"
    status = "reliable"
  } else if (!timingAllowed) {
    label = "Unreliable"
    signalLabel = "Unreliable"
    status = "unreliable"
  }
  ctx.cache.analysisQuality = {
    status,
    label,
    signalLabel,
    cycles,
    visualCycles,
    usableCycles,
    visualUsableCycles,
    reliableCycles,
    summaryCycles: timingAllowed ? timingSummaryCycles : [],
    energyCycles: energyAllowed ? energyInterpretableRun : [],
    visualEnergyCycles: visualEnergyAllowed || visualEnergyProvisional ? visualEnergyRun : [],
    selectedTimingCycleIds,
    selectedTimingCycleCount,
    energyCycleIds,
    visualEnergyCycleIds,
    energyCycleAlignment: {
      schema: "medaica.pcg.energy_cycle_alignment.v1",
      policy: "energy_scoring_uses_exact_timing_summary_cycles",
      timingCycleIds: selectedTimingCycleIds,
      energyCycleIds,
      visualEnergyCycleIds,
      visualPhaseAssignmentLimited: visualEnergyPhaseLimited,
      aligned: energyCycleIds.length
        ? energyCycleIds.length === selectedTimingCycleIds.length &&
          energyCycleIds.every((id, i) => id === selectedTimingCycleIds[i])
        : null,
      visualAligned: visualEnergyCycleIds.length
        ? visualEnergyCycleIds.length === selectedTimingCycleIds.length &&
          visualEnergyCycleIds.every((id, i) => id === selectedTimingCycleIds[i])
        : null,
    },
    longestRun: bestRun.length,
    longestReliableRun: bestReliableRun.length,
    timingSeconds: timingSummaryAllowed ? timingSummarySeconds : timingSeconds,
    energySeconds,
    visualEnergySeconds,
    reliableSeconds,
    markerConfidence,
    rawMarkerConfidence,
    timingCycleConfidence,
    energyMarkerConfidence,
    visualEnergyMarkerConfidence,
    timingSegment,
    energySegment,
    visualEnergySegment,
    signalReliable,
    timingAllowed,
    energyAllowed,
    visualEnergyAllowed,
    visualEnergyProvisional,
    visualEnergyLimited,
    phaseEnergyStatus: phaseEnergyDisplay.status,
    phaseEnergyMinSelectedCycles: MIN_ENERGY_RELIABLE_CYCLES,
    murmurBandEnergyEvidence: murmurEnergyEvidence,
    visualEnergyPhaseLimited: phaseEnergyDisplay.phaseAssignmentLimited,
    usablePct,
    noisyPct: noisyCoveragePct,
    noiseWindows: mergedNoiseWindows,
    guideQualityWindows: mergedGuideQualityWindows,
    summaryAllowed: timingAllowed,
    rawSummaryAllowed: timingAllowed,
    reason: timingAllowed
      ? timingSummaryAllowed
        ? ""
        : "Timing summary uses all usable cycles because there were not enough high-confidence cycles"
      : usableCycles.length < MIN_USABLE_CYCLES
      ? `Needs ${MIN_USABLE_CYCLES} usable complete cycles`
      : "Not enough usable guide-quality audio",
    energyReason: energyAllowed
      ? ""
      : limitedByAtypicalBurst
      ? "S1/S2 guide morphology atypical; phase-linked Energy summary suppressed"
      : !timingAllowed
      ? "Timing guide unavailable"
      : timingSummaryCycles.length < MIN_ENERGY_RELIABLE_CYCLES
      ? `Needs ${MIN_ENERGY_RELIABLE_CYCLES} selected Timing cycles for Energy scoring`
      : !coverageOk
      ? "Whole-recording timing coverage is outside the accepted range"
      : "Selected Timing cycle set is not sufficiently regular for strict Energy evidence",
    visualEnergyReason: visualEnergyAllowed
      ? ""
      : !timingAllowed
      ? "Timing guide unavailable"
      : visualEnergyProvisional
      ? `Provisional estimate based on ${selectedTimingCycleCount} selected Timing cycles, below the automated acceptance threshold of ${MIN_ENERGY_RELIABLE_CYCLES}. Manual review is required.`
      : visualEnergyLimited
      ? `Moderate-or-higher murmur-band Energy is visible, but there are only ${selectedTimingCycleCount} selected Timing cycles, below the minimum of ${MIN_ENERGY_RELIABLE_CYCLES} required for SYS/DIA phase scoring.`
      : timingSummaryCycles.length < MIN_ENERGY_RELIABLE_CYCLES
      ? `Needs ${MIN_ENERGY_RELIABLE_CYCLES} selected Timing cycles for Energy display`
      : "Structural-sound guide limited",
  }
  return ctx.cache.analysisQuality
}
